import { useAccess } from "@/utils/access-provider";
import { useApiSchema } from "@/utils/api-schema-provider";
import { useSidebarContext, SidebarProvider } from "@/hooks/use-sidebar";
import { useEffect, useState, useMemo } from "react";

import ArcSidebar from "./arc-sidebar";
import ImpersonationBanner from "@/components/impersonation-banner";
import PreviewOverlay from "./preview-overlay";
import DataChat from "@/components/data-chat";
import CreateWorkspaceModal from "./create-workspace-modal";
import { GlobalSearchProvider } from "@/components/global-search";
import { useDashboards } from "@/api/dashboard";
import { useWorkspace } from "@/hooks/use-workspace";
// Split view components
import {
  SplitViewProvider,
  SplitViewRenderer,
} from "./split-view";

// Collapsed sidebar width - must match arc-sidebar COLLAPSED_WIDTH
const COLLAPSED_SIDEBAR_WIDTH = 56;

/**
 * Inner layout component that uses sidebar context.
 * Arc-style design with modern gradients and spacing.
 */
const ArcLayoutContent: React.FC = () => {
  const {
    toggleLock,
    previewState,
    cancelPreview,
    confirmPreview,
    isLocked,
    sidebarWidth,
  } = useSidebarContext();

  const { isImpersonating, impersonationState, stopImpersonation, accessToken } =
    useAccess();

  const { data: dashboards } = useDashboards();
  const { hasWorkspace, isLoading: isWorkspaceLoading, isError: isWorkspaceError } = useWorkspace();

  // Get preview item info (now using item IDs directly)
  const previewItemId = previewState.previewItemId;

  // Get active dashboard for preview
  const previewDashboard = useMemo(() => {
    if (!previewItemId || !dashboards) return null;
    return dashboards.find((d) => d.id === previewItemId) || null;
  }, [previewItemId, dashboards]);

  // Keyboard shortcut for sidebar lock toggle (⌘B)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "b") {
        e.preventDefault();
        toggleLock();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [toggleLock]);

  return (
    <SplitViewProvider>
      <div className="flex h-screen w-screen overflow-hidden bg-surface-tinted">
        {/* Arc Sidebar - always visible (expanded or collapsed) */}
        <ArcSidebar />

        {/* Main Content - offset by sidebar width (full width when locked, collapsed width otherwise) */}
        <main
          className="flex flex-1 flex-col max-w-full w-full transition-[margin] duration-arc-normal ease-arc"
          style={{
            marginLeft: isLocked ? sidebarWidth : COLLAPSED_SIDEBAR_WIDTH,
          }}
        >
          {/* Impersonation Banner */}
          {isImpersonating && impersonationState && (
            <ImpersonationBanner
              impersonatedUser={impersonationState.impersonatedUser}
              onStopImpersonating={stopImpersonation}
            />
          )}

          {/* Content Area - Arc-style gradient background with workspace theme tint */}
          <div
            className="
              flex-1
              overflow-hidden
              min-h-0
              bg-gradient-to-br from-surface-tinted via-surface-tinted-secondary to-surface-tinted
              overscroll-x-none
            "
          >
            {/* Normal mode with preview overlay */}
            <PreviewOverlay
              isActive={previewState.isPreviewActive}
              tabName={previewDashboard?.name || ""}
              tabColor="var(--arc-accent)"
              onConfirm={confirmPreview}
              onCancel={cancelPreview}
            >
              {/* Split view renderer - always renders content through panels */}
              <SplitViewRenderer />
            </PreviewOverlay>
          </div>

          {/* Modal portal container - modals render here for content-aware centering */}
          <div
            id="modal-root"
            className="fixed inset-0 pointer-events-none z-[10000] transition-[left] duration-arc-normal ease-arc"
            style={{
              left: isLocked ? sidebarWidth : COLLAPSED_SIDEBAR_WIDTH,
            }}
          />
        </main>

        {/* Global DataChat - accessible from all pages */}
        <DataChat />

        {/* Create Workspace Modal - shown when user has no workspaces */}
        <CreateWorkspaceModal open={!!accessToken && !isWorkspaceError && !isWorkspaceLoading && !hasWorkspace} />
      </div>
    </SplitViewProvider>
  );
};

/**
 * Arc-style layout component with sidebar.
 * Wraps content with SidebarProvider for state management.
 *
 * Features:
 * - Modern Arc Browser-inspired design
 * - Responsive sidebar with lock/unlock
 * - Keyboard shortcuts (⌘B to toggle sidebar)
 * - Preview mode for dashboards
 * - Impersonation banner support
 * - All content is rendered through the panel system (no children needed)
 */
const ArcLayout: React.FC = () => {
  const { requiresAuth } = useAccess();
  const { apiSchema } = useApiSchema();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    setIsAuthenticated(requiresAuth());
  }, [requiresAuth]);

  if (!isAuthenticated || !apiSchema) return <></>;

  return (
    <SidebarProvider>
      <GlobalSearchProvider>
        <ArcLayoutContent />
      </GlobalSearchProvider>
    </SidebarProvider>
  );
};

export default ArcLayout;
